/**
 * Firestore query layer for parkings. Identical surface to app/src/services/parkings.ts.
 */
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { geohashQueryBounds } from 'geofire-common';
import { db } from './firebase';
import type { Parking } from '../types';

/** [south, west, north, east] — same order used across shared/ and functions/. */
export type BBox = [number, number, number, number];

const PARKINGS = 'parkings';

export async function fetchParking(parkingId: string): Promise<Parking | null> {
  const snap = await getDoc(doc(db, PARKINGS, parkingId));
  if (!snap.exists()) return null;
  return snap.data() as Parking;
}

function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const R = 6371000;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Queries /parkings for the given viewport via geofire-common's geohash
 * range bounds (one Firestore query per bound pair, merged and de-duped),
 * then filters client-side to the exact bbox and drops hidden parkings.
 */
export async function fetchParkingsInViewport(bbox: BBox): Promise<Parking[]> {
  const [south, west, north, east] = bbox;
  const center: [number, number] = [(south + north) / 2, (west + east) / 2];
  const radiusM = Math.max(
    distanceMeters({ lat: center[0], lng: center[1] }, { lat: south, lng: west }),
    distanceMeters({ lat: center[0], lng: center[1] }, { lat: north, lng: east })
  );
  const bounds = geohashQueryBounds(center, radiusM);

  const snapshots = await Promise.all(
    bounds.map(([start, end]) =>
      getDocs(query(collection(db, PARKINGS), where('geohash', '>=', start), where('geohash', '<=', end)))
    )
  );

  const seen = new Map<string, Parking>();
  for (const snap of snapshots) {
    for (const d of snap.docs) {
      const data = d.data() as Parking & { hidden?: boolean };
      if (data.lat < south || data.lat > north || data.lng < west || data.lng > east) continue;
      if (data.hidden) continue;
      // Include document ID in the parking object
      seen.set(d.id, { ...data, id: d.id });
    }
  }
  return [...seen.values()];
}

export const haversineKm = (
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number => {
  const R = 6371;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
};

export function withDistance(
  parkings: Parking[],
  reference: { lat: number; lng: number }
): Array<Parking & { distanceKm: number }> {
  return parkings
    .map((p) => ({ ...p, distanceKm: haversineKm(reference, { lat: p.lat, lng: p.lng }) }))
    .sort((a, b) => a.distanceKm - b.distanceKm);
}
